import  React, { ChangeEvent, useMemo } from 'react';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import Table from '../Table';
import Loader from '../Loader';
import Skeleton from 'src/components/Skeleton/Skeleton';
import Search from 'src/components/Search/Search';
import ContactForm from 'src/components/ContactForm/ContactForm';
import { ITableColumn } from 'src/components/Table/types';
import {
  sendActivationLink,
  sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import { SuiteUserRow } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/types';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import './SuiteUsersTable.scss';

const SKELETON_ROW_COUNT: number = 13;

interface SuiteUsersTableProps {
  suite: any;
  suiteUsers: SuiteUserRow[];
  columns: ITableColumn[];
  loading: boolean;
  formLoaderVisible: boolean;
  showOverlay: boolean;
  editingExistingContactInTable: boolean;
  allowedContactFormRoles: string[];
  canAddContact: boolean;
  searchIsActive: string;
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  onOpenAddContact: () => void;
  onCloseContactForm: () => void;
  onSubmitContactForm: (
    formData: IContactFormData,
    formType: string
  ) => Promise<void>;
}

const SuiteUsersTable = ({
  suite,
  suiteUsers,
  columns,
  loading,
  formLoaderVisible,
  showOverlay,
  editingExistingContactInTable,
  allowedContactFormRoles,
  canAddContact,
  searchIsActive,
  onSearch,
  onOpenAddContact,
  onCloseContactForm,
  onSubmitContactForm,
}: SuiteUsersTableProps) => {
  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const skeletonColumns: ITableColumn[] = useMemo(
    () =>
      columns.map((column: ITableColumn) => ({
        ...column,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [columns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_, index: number) => ({
        id: `suite-user-skeleton-${index}`,
      })),
    []
  );

  return (
    <>
      {formLoaderVisible ? <Loader center={true} /> : null}
      <div className="suite-users-table-header">
        <div className="suite-users-table-search">
          <Search
            size="small"
            placeholder="Search People..."
            onChange={onSearch}
            theme={undefined}
          />
        </div>
        {canAddContact ? (
          <Button
            color="teal"
            label="Add Person"
            onClick={onOpenAddContact}
            icon={
              <FontAwesomeIcon
                glyph="user-plus"
                weight="regular"
                size="small"
              />
            }
            size="small"
          />
        ) : null}
      </div>
      {loading ? (
        <Table
          columns={skeletonColumns}
          data={skeletonData}
          paginate={{ itemsPerPage, position: 'both' }}
          paginationSkeleton
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : suiteUsers && suiteUsers.length ? (
        <Table
          columns={columns}
          // legacy/workaround: Table's data union doesn't include SuiteUserRow yet
          data={suiteUsers as any}
          paginate={{ itemsPerPage, position: 'both' }}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : (
        <Empty
          direction="ttb"
          headline="No people yet"
          strapline="Add your people here so it's easier for you to manage."
          searchIsActive={searchIsActive}
          cta={
            canAddContact ? (
              <Button
                color="teal"
                label="Add Person"
                onClick={onOpenAddContact}
                icon={
                  <FontAwesomeIcon glyph="user" weight="light" size="small" />
                }
                size="small"
              />
            ) : null
          }
          image={<img alt="No people yet" src="/assets/empty/contacts.svg" />}
        />
      )}
      {showOverlay && (
        <ContactForm
          onClose={onCloseContactForm}
          onSubmit={onSubmitContactForm}
          sendResetPasswordLinkToUser={sendResetPasswordLink}
          sendActivationLinkToUser={sendActivationLink}
          allowedRoles={allowedContactFormRoles}
          pageUsedFrom="Suites"
          editingExistingContactInTable={editingExistingContactInTable}
          currentSuiteIdForAddingExistingHomeowner={
            suite.number || suite.name
          }
        />
      )}
    </>
  );
};

export default SuiteUsersTable;
